import type {SyntheticEvent} from 'react'
import {useCallback, useMemo, useState} from 'react'
import {Box, Button, LinearProgress, Rating, Stack, styled, Typography} from '@mui/material'
import type {MSLQAnswer, MSLQQuestion, MSLQSurveyResponse} from './model'
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

import {type DocumentReference, updateDoc} from 'firebase/firestore'
import {orderBy} from 'lodash'
import questions from './questions_hu.json'
import {useFirestoreSnapshot} from '@goschool/react-firebase'
import {Trans, useTranslation} from 'react-i18next'

const scaleSize = 7
const pageSize = 9

export interface MSLQFormProps {
  responseReference: DocumentReference<MSLQSurveyResponse>
}

export function MSLQForm({responseReference}: MSLQFormProps) {
  const sortedQuestions = useMemo((): MSLQQuestion[] => {
    return orderBy(questions, (q) => q.id)
  }, [])

  const {getAnswer, setAnswer, saveResponse, status} = useResponseHandler(responseReference)

  const [page, setPage] = useState(0)

  const {pageQuestions, totalPages} = useMemo(() => {
    const start = page * pageSize
    const end = start + pageSize
    const totalPages = Math.ceil(questions.length / pageSize)
    const pageQuestions = sortedQuestions.slice(start, end)
    return {pageQuestions, totalPages}
  }, [page, sortedQuestions])

  const selectAnswer = useCallback(
    (question: MSLQQuestion) => (value: number | null) =>
      setAnswer(question, value),
    [setAnswer]
  )

  const allowNext = useMemo(
    () => pageQuestions.every((q) => getAnswer(q) != null),
    [getAnswer, pageQuestions]
  )

  const handleNext = useCallback(() => {
    setPage((curr) => Math.min(curr + 1, totalPages - 1))
  }, [totalPages])

  const handleBack = useCallback(() => {
    setPage((curr) => Math.max(curr - 1, 0))
  }, [])


  const allowEvaluate = useMemo(
    () =>
      sortedQuestions.map(getAnswer).filter(notEmpty).length ===
      sortedQuestions.length,
    [getAnswer, sortedQuestions]
  )

  if (status === 'completed') {
    return <ThankYou/>
  }


  return <>
    <Typography variant="h5" mb={2}>
      <Trans i18nKey="formTitle"/>
    </Typography>
    <Stack direction="row" alignItems="center" mb={2}>
      <LinearProgress
        sx={{flexGrow: 1, mr: 2}}
        variant="determinate"
        value={page / totalPages * 100}
      />

      <Box sx={{minWidth: 35}}>
        <Typography
          variant="body2"
          whiteSpace="nowrap"
          sx={{color: 'text.secondary'}}
        >{`${page + 1} / ${totalPages}`}</Typography>
      </Box>
    </Stack>

    {pageQuestions.map((q) =>
      <LikertSelect
        key={q.id}
        question={q}
        answer={getAnswer(q)?.value}
        setAnswer={selectAnswer(q)}
      />
    )}
    <Box mt={4} display="flex" justifyContent="space-between">
      <Button variant="outlined" onClick={handleBack} disabled={page === 0}>
        <Trans i18nKey="prevPage"/>
      </Button>
      {page < totalPages - 1 ?
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!allowNext}
        >
          <Trans i18nKey="nextPage"/>
        </Button>
        :
        <Button
          variant="contained"
          onClick={saveResponse}
          disabled={!allowEvaluate}>
          <Trans i18nKey="submit"/>
        </Button>
      }
    </Box>
  </>
}

interface LikertSelectProps {
  question: MSLQQuestion;
  answer: number | undefined;
  setAnswer: (answer: number | null) => void;
}

function LikertSelect(props: LikertSelectProps) {
  const {t} = useTranslation()
  const {question, answer, setAnswer} = props

  const text = useMemo(
    () => t(`questions:text.${question.id}`),
    [question, t]
  )

  const ratingLabel = useCallback((value: number) => {
    const labels = t('ratingScale', {returnObjects: true}) as string[]
    return labels[value - 1] as string
  }, [t])

  const [active, setActive] = useState<number>(-1)

  const handleAnswerChange = useCallback(
    (e: SyntheticEvent<Element, Event>, value: number | null) =>
      setAnswer(value),
    [setAnswer]
  )

  const displayed = useMemo(
    () => active === -1 ? answer : active,
    [answer, active]
  )

  return (
    <Box mb={3}>
      <Typography variant="body1" fontWeight="bold" gutterBottom={true}>
        {text}
      </Typography>
      <Stack direction="row" alignItems="center">
        <StyledRating
          icon={<RadioButtonCheckedIcon sx={{marginRight: '1em'}}/>}
          emptyIcon={<RadioButtonUncheckedIcon sx={{marginRight: '1em'}}/>}
          onChangeActive={(event, newHover) => {
            setActive(newHover)
          }}
          highlightSelectedOnly={true}
          color="primary"
          max={scaleSize}
          getLabelText={ratingLabel}
          value={answer ?? null}
          onChange={handleAnswerChange}
        />
        {displayed != null &&
            <Typography variant="body2" whiteSpace="nowrap" overflow="hidden"
              textOverflow="ellipsis">{ratingLabel(displayed)}</Typography>
        }
      </Stack>
    </Box>
  )
}

const StyledRating = styled(Rating)(({theme}) => ({
  '& .MuiRating-iconFilled': {
    color: theme.palette.primary.main
  }
}))

function notEmpty<T>(v: T | undefined | null): v is T {
  return v != null
}


function useResponseHandler(response: DocumentReference<MSLQSurveyResponse>) {
  const snapshot = useFirestoreSnapshot(response, {waitForWrites: false})

  const data = useMemo(
    () => snapshot?.data(),
    [snapshot]
  )

  const answers = useMemo(
    () => {
      if (data == null) {
        return null
      }

      const answers: Record<number, MSLQAnswer | undefined> = {}

      questions.forEach((q) => {
        const answer = data.answers.find((a) => a.question_id === q.id)
        if (answer != null) {
          answers[q.id] = answer
        }
      })

      return answers

    }, [data])

  const getAnswer = useCallback(
    (question: MSLQQuestion): MSLQAnswer | undefined => {
      if (answers == null) {
        return undefined
      }

      return answers[question.id]
    }, [answers])

  const setAnswer = useCallback(
    (question: MSLQQuestion, value: number | null) => {
      if (data == null) {
        return
      }

      const update = [...data.answers]
      const existingIndex = update.findIndex((a) => a.question_id === question.id)
      let hadChange = false

      if (value == null) {
        if (existingIndex !== -1) {
          update.splice(existingIndex, 1)
        }
        hadChange = true
      } else {
        const answer: MSLQAnswer = {
          question_id: question.id,
          scale_id: question.scale_id,
          value
        }
        if (existingIndex === -1) {
          update.push(answer)
          hadChange = true
        } else if (update[existingIndex].value !== value) {
          update[existingIndex].value = value
          hadChange = true
        }
      }
      if (hadChange) {
        updateDoc(response, {
          answers: update
        })
      }
    },
    [data, response]
  )

  const saveResponse = useCallback(
    async () => {
      updateDoc(response, {
        status: 'completed'
      })
    }, [response])

  const status = useMemo(() => {
    if (data == null) {
      return 'started'
    }
    return data.status
  }, [data])

  return {getAnswer, setAnswer, saveResponse, answers, status}
}


function ThankYou() {
  return (
    <Stack spacing={2}>
      <Typography variant="h5" mb={2}>
        <Trans i18nKey="mslqThankYou.title"/>
      </Typography>
      <Typography variant="body1">
        <Trans i18nKey="mslqThankYou.message"/>
      </Typography>
    </Stack>
  )
}
